(() => {
  const TOPICS = ['product', 'order', 'after_sales'];
  let active = null;
  const widgetControllers = new Map();
  const POSITION_KEY = 'care-widget-position:v1';
  const GAP = 12;
  const clamp = (value, min, max) => Math.min(Math.max(value, min), Math.max(min, max));
  let pointerDelegatesAttached = false;
  const moveWidgets = event => widgetControllers.forEach(controller => controller.moveDrag(event));
  const finishWidgets = event => widgetControllers.forEach(controller => controller.finishDrag(event));
  const attachPointerDelegates = () => {
    if (pointerDelegatesAttached) return;
    document.addEventListener('pointermove', moveWidgets, {passive:false});
    document.addEventListener('pointerup', finishWidgets);
    document.addEventListener('pointercancel', finishWidgets);
    pointerDelegatesAttached = true;
  };
  const detachPointerDelegates = () => {
    document.removeEventListener('pointermove', moveWidgets);
    document.removeEventListener('pointerup', finishWidgets);
    document.removeEventListener('pointercancel', finishWidgets);
    pointerDelegatesAttached = false;
  };
  const reflowWidgets = () => {
    widgetControllers.forEach((controller, widget) => {
      if (!widget.isConnected) controller.disconnect();
      else controller.reflow();
    });
  };
  const initializeWidgets = () => {
    document.querySelectorAll('[data-care-widget]').forEach(widget => {
      if (widgetControllers.has(widget)) return;
      const trigger = widget.querySelector('summary');
      const panel = widget.querySelector('[data-care-widget-panel]');
      if (!trigger || !panel) return;
      let position = null;
      let drag = null;
      let suppressClick = false;
      const listeners = [];
      const listen = (target, name, handler, options) => {
        if (!target) return;
        target.addEventListener(name, handler, options);
        listeners.push(() => target.removeEventListener(name, handler, options));
      };
      try {
        const saved = JSON.parse(localStorage.getItem(POSITION_KEY));
        if (saved && ['x', 'y'].every(axis => Number.isFinite(saved[axis]) && saved[axis] >= 0 && saved[axis] <= 1)) position = {x:saved.x, y:saved.y};
      } catch { /* Default placement also works when storage is unavailable. */ }
      const measure = () => {
        const viewport = window.visualViewport;
        const viewportLeft = viewport?.offsetLeft || 0;
        const viewportTop = viewport?.offsetTop || 0;
        const viewportWidth = viewport?.width || window.innerWidth;
        const viewportHeight = viewport?.height || window.innerHeight;
        const style = getComputedStyle(widget);
        const inset = side => Number.parseFloat(style.getPropertyValue('--care-safe-' + side)) || 0;
        const size = trigger.getBoundingClientRect();
        const bounds = {
          left:viewportLeft + GAP + inset('left'), top:viewportTop + GAP + inset('top'),
          right:viewportLeft + viewportWidth - GAP - inset('right'), bottom:viewportTop + viewportHeight - GAP - inset('bottom'),
          width:size.width, height:size.height, centerY:viewportTop + viewportHeight / 2
        };
        const sticky = document.querySelector('[data-sticky-purchase]');
        if (sticky && getComputedStyle(sticky).display !== 'none') {
          const rect = sticky.getBoundingClientRect();
          if (rect.width > 0 && rect.height > 0 && rect.top < bounds.bottom && rect.bottom > bounds.top) bounds.bottom = Math.min(bounds.bottom, rect.top - GAP);
        }
        bounds.maxX = Math.max(bounds.left, bounds.right - bounds.width);
        bounds.maxY = Math.max(bounds.top, bounds.bottom - bounds.height);
        return bounds;
      };
      const placePanel = (bounds, left, top) => {
        if (!widget.open) return;
        const width = Math.min(360, Math.max(0, bounds.right - bounds.left));
        const roomLeft = left - bounds.left;
        const roomRight = bounds.right - left - bounds.width;
        const side = roomLeft >= width + GAP ? 'left' : roomRight >= width + GAP ? 'right' : null;
        const above = Math.max(0, top - bounds.top - GAP);
        const below = Math.max(0, bounds.bottom - top - bounds.height - GAP);
        const abovePreferred = above >= below;
        panel.style.width = width + 'px';
        panel.style.maxHeight = Math.min(600, side ? bounds.bottom - bounds.top : Math.max(above, below)) + 'px';
        panel.style.right = 'auto';
        panel.style.bottom = 'auto';
        const height = panel.getBoundingClientRect().height;
        const panelLeft = side === 'left' ? left - width - GAP : side === 'right' ? left + bounds.width + GAP : left + bounds.width - width;
        const panelTop = side ? top + (bounds.height - height) / 2 : abovePreferred ? top - height - GAP : top + bounds.height + GAP;
        panel.style.left = (clamp(panelLeft, bounds.left, bounds.right - width) - left) + 'px';
        panel.style.top = (clamp(panelTop, bounds.top, bounds.bottom - height) - top) + 'px';
      };
      const place = (bounds, left, top) => {
        left = clamp(left, bounds.left, bounds.maxX);
        top = clamp(top, bounds.top, bounds.maxY);
        widget.dataset.positioned = 'true';
        widget.style.left = left + 'px';
        widget.style.top = top + 'px';
        placePanel(bounds, left, top);
        return {left, top};
      };
      const reflow = () => {
        const bounds = measure();
        const left = position ? bounds.left + position.x * (bounds.maxX - bounds.left) : bounds.maxX;
        const top = position ? bounds.top + position.y * (bounds.maxY - bounds.top) : bounds.centerY - bounds.height / 2;
        place(bounds, left, top);
      };
      const remember = () => {
        try { localStorage.setItem(POSITION_KEY, JSON.stringify(position)); } catch { /* Keep the position for this page only. */ }
      };
      listen(trigger, 'pointerdown', event => {
        if (event.isPrimary === false || event.button !== 0) return;
        event.preventDefault();
        trigger.focus({preventScroll:true});
        suppressClick = false;
        const rect = trigger.getBoundingClientRect();
        drag = {id:event.pointerId, x:event.clientX, y:event.clientY, left:rect.left, top:rect.top, moved:false};
        try { trigger.setPointerCapture(event.pointerId); } catch { /* Native pointer delivery remains available. */ }
      });
      const moveDrag = event => {
        if (!drag || drag.id !== event.pointerId) return;
        const dx = event.clientX - drag.x;
        const dy = event.clientY - drag.y;
        if (!drag.moved && Math.hypot(dx, dy) < 6) return;
        drag.moved = true;
        suppressClick = true;
        widget.classList.add('care-widget--dragging');
        if (event.cancelable) event.preventDefault();
        const bounds = measure();
        const point = place(bounds, drag.left + dx, drag.top + dy);
        position = {
          x:(point.left - bounds.left) / (bounds.maxX - bounds.left || 1),
          y:(point.top - bounds.top) / (bounds.maxY - bounds.top || 1)
        };
      };
      const finishDrag = event => {
        if (!drag || drag.id !== event.pointerId) return;
        const finished = drag;
        drag = null;
        widget.classList.remove('care-widget--dragging');
        if (finished.moved) remember();
        try { if (trigger.hasPointerCapture(event.pointerId)) trigger.releasePointerCapture(event.pointerId); } catch { /* Capture may already be released. */ }
      };
      listen(trigger, 'lostpointercapture', event => {
        // Continue through document if capture is lost while still pressed.
        if (event.buttons === 1) return;
        finishDrag(event);
      });
      listen(trigger, 'click', event => {
        if (suppressClick && event.detail !== 0) { event.preventDefault(); event.stopImmediatePropagation(); }
        suppressClick = false;
      }, true);
      listen(widget, 'toggle', reflow);
      listen(widget.querySelector('[data-care-widget-close]'), 'click', () => { widget.open = false; trigger.focus({preventScroll:true}); });
      listen(widget.querySelector('[data-care-widget-reset]'), 'click', () => {
        position = null;
        try { localStorage.removeItem(POSITION_KEY); } catch { /* Reset still works without storage. */ }
        reflow();
      });
      const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(reflow) : null;
      const sticky = document.querySelector('[data-sticky-purchase]');
      if (sticky) observer?.observe(sticky);
      observer?.observe(panel);
      const cancelDrag = () => { if (drag) finishDrag({pointerId:drag.id}); };
      widgetControllers.set(widget, {reflow, moveDrag, finishDrag, cancelDrag, disconnect:() => {
        cancelDrag(); listeners.forEach(remove => remove()); observer?.disconnect(); widgetControllers.delete(widget);
        if (!widgetControllers.size) detachPointerDelegates();
      }});
      attachPointerDelegates();
      reflow();
    });
    reflowWidgets();
  };
  const normalizeAccount = (account) => {
    if (!account || account.enabled !== true || !['whatsapp', 'telegram'].includes(account.channel)) return null;
    let destination = String(account.destination || '').trim();
    if (/^https?:\/\//i.test(destination)) {
      try {
        const url = new URL(destination);
        const hosts = account.channel === 'whatsapp' ? ['wa.me'] : ['t.me', 'telegram.me'];
        if (url.protocol !== 'https:' || !hosts.includes(url.hostname) || url.port || url.username || url.password || url.search || url.hash) return null;
        destination = url.pathname.slice(1);
      } catch { return null; }
    }
    if (account.channel === 'whatsapp') {
      destination = destination.replace(/[+\s()\-]/g, '');
      if (!/^[1-9][0-9]{5,14}$/.test(destination)) return null;
    } else {
      if (destination.startsWith('+')) {
        if (/[\x00-\x1f]/.test(destination)) return null;
        destination = destination.replace(/[\s()\-]/g, '');
        if (!/^\+[1-9][0-9]{5,14}$/.test(destination)) return null;
      } else {
        destination = destination.replace(/^@/, '');
        if (!/^[A-Za-z][A-Za-z0-9_]{4,31}$/.test(destination)) return null;
      }
    }
    return {...account, destination, topics:Array.isArray(account.topics) ? account.topics.filter(topic => TOPICS.includes(topic)) : []};
  };
  const initialize = () => {
    initializeWidgets();
    const element = document.getElementById('SupportConfig');
    if (!element) return;
    let config;
    try { config = JSON.parse(element.getAttribute('data-config')); } catch { return; }
    const copy = config.copy || {};
    const accounts = Array.isArray(config.accounts) && config.enabled !== false ? config.accounts.map(normalizeAccount).filter(Boolean) : [];
    let topic = document.querySelector('[data-care-page] input[type="radio"]:checked')?.value || 'product';
    let endpoint = null;
    try {
      if (config.routingEndpoint) {
        const candidate = new URL(config.routingEndpoint, location.origin);
        const local = candidate.origin === location.origin && ['localhost', '127.0.0.1'].includes(candidate.hostname);
        if ((candidate.protocol === 'https:' || local) && !candidate.username && !candidate.password && !candidate.search && !candidate.hash) endpoint = candidate.href.replace(/\/$/, '');
      }
    } catch { /* Invalid endpoints use the per-browser fallback. */ }
    const eligible = channel => accounts.filter(account => account.channel === channel && account.topics.includes(topic));
    const accountUrl = account => account.channel === 'telegram' ? `https://t.me/${account.destination}` : `https://wa.me/${account.destination}?text=${encodeURIComponent(copy[topic] || '')}`;
    const routedUrl = channel => `${endpoint}/${channel}?topic=${encodeURIComponent(topic)}&lang=${encodeURIComponent(config.locale || 'en')}`;
    const directChoice = (channel, consume) => {
      const items = eligible(channel);
      if (!items.length) return null;
      if (config.mode === 'random') return items[Math.floor(Math.random() * items.length)];
      const key = `luxe-support:${channel}:${topic}`;
      let previous = null;
      try { previous = localStorage.getItem(key); } catch { /* Memory fallback works with blocked storage. */ }
      previous ||= initialize.last?.[key];
      const index = (items.findIndex(account => account.id === previous) + 1) % items.length;
      const next = items[index];
      if (consume) {
        initialize.last ||= {};
        initialize.last[key] = next.id;
        try { localStorage.setItem(key, next.id); } catch { /* Kept for this page only. */ }
      }
      return next;
    };
    const email = typeof config.email === 'string' && /^[^\s<>@]+@[^\s<>@]+\.[^\s<>@]+$/.test(config.email) && !/[\x00-\x1f]/.test(config.email) && config.enabled !== false ? config.email : '';
    const update = () => {
      ['whatsapp', 'telegram'].forEach(channel => {
        const items = eligible(channel);
        document.querySelectorAll(`[data-care-channel="${channel}"]`).forEach(card => card.classList.toggle('care-channel--unavailable', !items.length));
        document.querySelectorAll(`[data-care-link="${channel}"]`).forEach(link => {
          const label = link.querySelector('span') || link;
          const widget = link.classList.contains('care-widget__link');
          link.hidden = !widget && config.mode === 'manual' && items.length > 0;
          label.textContent = widget ? `${channel === 'whatsapp' ? 'WhatsApp' : 'Telegram'} · ${items.length ? copy.connect : copy.unavailable}` : (items.length ? copy.connect : copy.unavailable);
          if (items.length) {
            link.removeAttribute('aria-disabled');
            if (config.mode === 'manual') link.href = document.querySelector('[data-care-page]') ? '#care-' + channel : (document.querySelector('[data-care-widget] .care-widget__link')?.href || '/pages/contact');
            else link.href = endpoint ? routedUrl(channel) : accountUrl(directChoice(channel, false));
          } else {
            link.removeAttribute('href'); link.setAttribute('aria-disabled', 'true');
          }
        });
        document.querySelectorAll(`[data-care-agents="${channel}"]`).forEach(container => {
          container.id = 'care-' + channel;
          container.replaceChildren(); container.hidden = config.mode !== 'manual' || !items.length;
          if (container.hidden) return;
          const prompt = document.createElement('p'); prompt.textContent = copy.choose; container.append(prompt);
          items.forEach(account => {
            const link = document.createElement('a'); link.className = 'care-channel__action';
            link.textContent = account.label || (channel === 'whatsapp' ? 'WhatsApp' : 'Telegram');
            link.href = accountUrl(account); link.target = '_blank'; link.rel = 'noopener noreferrer'; container.append(link);
          });
        });
      });
      document.querySelectorAll('[data-care-email]').forEach(link => {
        link.hidden = !email;
        if (email) { link.href = `mailto:${email}?subject=${encodeURIComponent(copy[topic] || '')}`; link.removeAttribute('aria-disabled'); }
        else { link.removeAttribute('href'); link.setAttribute('aria-disabled', 'true'); }
      });
      document.querySelectorAll('[data-care-channel="email"]').forEach(card => { card.hidden = !email; });
      document.querySelectorAll('[data-care-hours]').forEach(node => { node.textContent = config.serviceHours || ''; node.hidden = !config.serviceHours || config.enabled === false; });
      document.querySelectorAll('[data-care-empty]').forEach(node => { node.hidden = Boolean(eligible('whatsapp').length || eligible('telegram').length || email); });
      reflowWidgets();
    };
    active = {
      changeTopic(value) { if (TOPICS.includes(value)) { topic = value; update(); } },
      follow(link, event) {
        const channel = link.dataset.careLink;
        if (!eligible(channel).length) { event.preventDefault(); return; }
        if (config.mode === 'manual') { link.removeAttribute('target'); return; }
        if (!endpoint) link.href = accountUrl(directChoice(channel, true));
      }
    };
    update();
  };
  document.addEventListener('change', event => {
    if (event.target.matches?.('[data-care-page] input[type="radio"]') && event.target.checked) active?.changeTopic(event.target.value);
  });
  document.addEventListener('click', event => {
    const link = event.target.closest?.('[data-care-link]');
    if (link) active?.follow(link, event);
  });
  document.addEventListener('keydown', event => {
    const widget = event.target.closest?.('[data-care-widget]') || document.querySelector('[data-care-widget][open]');
    if (event.key === 'Escape' && widget?.open) { event.preventDefault(); widget.open = false; widget.querySelector('summary')?.focus({preventScroll:true}); }
  });
  window.addEventListener('resize', reflowWidgets);
  window.addEventListener('orientationchange', reflowWidgets);
  window.addEventListener('blur', () => widgetControllers.forEach(controller => controller.cancelDrag()));
  window.visualViewport?.addEventListener('resize', reflowWidgets);
  window.visualViewport?.addEventListener('scroll', reflowWidgets);
  document.addEventListener('DOMContentLoaded', initialize, {once:true});
  document.addEventListener('shopify:section:load', initialize);
  document.addEventListener('shopify:section:unload', event => {
    widgetControllers.forEach((controller, widget) => { if (event.target.contains?.(widget)) controller.disconnect(); });
  });
})();
